/**
 * iter318 wave 13 — VyomOS Contact page (Spec §12).
 * Static form UX only. Backend `/api/public/contact` lead capture ships in Phase B.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useWebsiteContent } from '../../../hooks/useWebsiteContent';

export default function VyomosContact() {
  const { content } = useWebsiteContent('contact');
  const s = content?.settings || {};
  const contactEmail = s.contact_email || 'hello@vyomos.io';
  const contactPhone = s.contact_phone || '+1 (415) 555-0100';
  const contactAddress = s.contact_address || 'San Francisco · Bengaluru · Remote-first team';
  const [form, setForm] = useState({
    name: '',
    org: '',
    email: '',
    phone: '',
    role: '',
    participants: '',
    interest: 'demo',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Backend lead capture wired in Phase B — for now, surface a confirmation.
    setTimeout(() => {
      setSubmitting(false);
      toast.success('Thanks — a member of the VyomOS team will reach out within one business day.');
      setForm({ name: '', org: '', email: '', phone: '', role: '', participants: '', interest: 'demo', message: '' });
    }, 700);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="vyomos-contact">
      {/* Header */}
      <section className="bg-gradient-to-b from-[#EAF5FF] to-[#F8FBFF]">
        <div className="max-w-6xl mx-auto px-6 py-20 text-center">
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1677FF] mb-3">Contact</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">Let&apos;s talk about your operation.</h1>
          <p className="text-lg text-[#566579] max-w-2xl mx-auto">
            Share a few details and we&apos;ll set up a working session tailored to your programs, facilities, and growth plans.
          </p>
        </div>
      </section>

      {/* Form + info */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-5 gap-10">
          {/* Info column */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">Get in touch</h2>
              <p className="text-[#566579]">
                Prefer a direct channel? Reach us through any of the options below.
              </p>
            </div>
            <div className="space-y-4">
              <InfoRow icon={<Mail className="h-5 w-5 text-[#1677FF]" />} label="Email" value={contactEmail} />
              <InfoRow icon={<Phone className="h-5 w-5 text-[#1677FF]" />} label="Phone" value={contactPhone} />
              <InfoRow icon={<MapPin className="h-5 w-5 text-[#1677FF]" />} label="Address" value={contactAddress} />
            </div>
            <div className="bg-[#F8FBFF] border border-[#DCE6F0] rounded-2xl p-6">
              <h3 className="font-semibold mb-2">Prefer to explore first?</h3>
              <p className="text-sm text-[#566579] mb-4">
                Spin up a 30-minute interactive demo — no sales conversation required.
              </p>
              <a
                href="https://demo.vyomos.io"
                data-testid="contact-cta-demo"
                className="inline-block px-5 py-2 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] text-white text-sm font-semibold transition"
              >
                Experience VyomOS →
              </a>
            </div>
          </div>

          {/* Form column */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-3 bg-white border border-[#DCE6F0] rounded-3xl p-8 shadow-sm"
          >
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <Field label="Your name" required>
                <input required value={form.name} onChange={set('name')} data-testid="contact-name" className="input" />
              </Field>
              <Field label="Organization" required>
                <input required value={form.org} onChange={set('org')} data-testid="contact-org" className="input" />
              </Field>
              <Field label="Email" required>
                <input required type="email" value={form.email} onChange={set('email')} data-testid="contact-email" className="input" />
              </Field>
              <Field label="Phone">
                <input value={form.phone} onChange={set('phone')} data-testid="contact-phone" className="input" />
              </Field>
              <Field label="Your role">
                <select value={form.role} onChange={set('role')} data-testid="contact-role" className="input">
                  <option value="">Select…</option>
                  <option>Owner / Founder</option>
                  <option>Operations Lead</option>
                  <option>Program Director</option>
                  <option>Facility Manager</option>
                  <option>Coach / Trainer</option>
                  <option>Other</option>
                </select>
              </Field>
              <Field label="Active participants">
                <select value={form.participants} onChange={set('participants')} data-testid="contact-size" className="input">
                  <option value="">Select…</option>
                  <option>Under 100</option>
                  <option>100–500</option>
                  <option>500–2,000</option>
                  <option>2,000+</option>
                </select>
              </Field>
            </div>
            <Field label="I&apos;m most interested in">
              <div className="flex flex-wrap gap-2">
                {[
                  ['demo', 'Product demo'],
                  ['pricing', 'Pricing conversation'],
                  ['migration', 'Migrating from another platform'],
                  ['custom', 'Custom workflows'],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, interest: v }))}
                    className={
                      form.interest === v
                        ? 'px-4 py-2 rounded-full bg-[#1677FF] text-white text-sm font-medium'
                        : 'px-4 py-2 rounded-full border border-[#DCE6F0] hover:border-[#55AEEF] text-sm text-[#566579]'
                    }
                  >
                    {l}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Anything else we should know?">
              <textarea
                rows={4}
                value={form.message}
                onChange={set('message')}
                data-testid="contact-message"
                className="input"
                placeholder="Tell us about your programs, facilities, tools you currently use…"
              />
            </Field>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-2 px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] disabled:opacity-60 text-white font-semibold transition"
            >
              {submitting ? 'Sending…' : 'Send message'}
            </button>
            <p className="text-xs text-[#566579] mt-4">
              We reply within one business day. By submitting you agree to our privacy policy.
            </p>
          </form>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          padding: 10px 14px;
          border: 1px solid #DCE6F0;
          border-radius: 12px;
          font-size: 14px;
          background: #fff;
          color: #10233F;
          transition: border-color .15s ease;
        }
        .input:focus { outline: none; border-color: #1677FF; box-shadow: 0 0 0 3px rgba(22,119,255,.12); }
      `}</style>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-xl bg-[#EAF5FF] flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-wider text-[#566579]">{label}</div>
        <div className="font-medium text-[#10233F]">{value}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-[#10233F] mb-1.5">
        {label} {required && <span className="text-[#1677FF]">*</span>}
      </label>
      {children}
    </div>
  );
}
